import {
	type MotionValue,
	animate,
	useMotionValue,
	useMotionValueEvent,
	useReducedMotion,
	useScroll,
} from "framer-motion";
import { startTransition, useLayoutEffect, useRef, useState } from "react";
import { clamp, easeInOut, lerp } from "../../lib/motion";
import {
	SECTION_FOUR_OVERLAP_UNITS,
	SECTION_THREE_MIN_PINNED_DWELL_UNITS,
	linkedProgress,
} from "../../scroll/landing-scroll-timeline";
import { CHAT_PLAYBACK_CLOCK_PAD_MS, computeChatPlaybackDurationMs } from "../two/chat/chat-playback";
import { SECTION_TWO_CHAT_MESSAGES, SECTION_TWO_TITLE_BLOCK_ID } from "../two/chat/chat-transcript";

export type TChatStatus = "idle" | "playing" | "complete";
export type TSectionTwoIntro = "idle" | "title-playing" | "chat-playing";

export type TSectionThreeScene = {
	skyY: MotionValue<number>;
	distantY: MotionValue<number>;
	chatY: MotionValue<number>;
	foregroundY: MotionValue<number>;
	eveningOpacity: MotionValue<number>;
	chatScale: MotionValue<number>;
	sectionThreeScrimOpacity: MotionValue<number>;
	sectionTwoIntro: TSectionTwoIntro;
	chatStatus: TChatStatus;
};

type TSectionThreeLayoutCache = {
	viewportHeight: number;
	isMobile: boolean;
	sectionFourEl: HTMLElement | null;
	titleBlockEl: HTMLElement | null;
};

/* Scoped choreography budget (viewport-height units). The landscape drifts into
 its depth pose while the section slides into the pin; the moment the stage
 touches the top of the viewport the whole scene — content included — starts
 receding with scroll, and S4 meets the fold a beat later. PIN_HOLD keeps
 the sticky stage pinned for one extra viewport after full cover so it does not
 unpin mid-handoff.

 SECTION_FOUR_OVERLAP_UNITS is how far S4 pulls up over this runway. It resolves
 through two caps — S4's measured height, and a minimum pinned dwell — so the
 desk meets the fold ~0.1 viewports after the pin. */
const CHAT_WORLD_UNITS = 1.1;
const COVER_UNITS = 1;
const PIN_HOLD_UNITS = 1;
const RUNWAY_UNITS = CHAT_WORLD_UNITS + COVER_UNITS;
export const SECTION_THREE_RUNWAY_HEIGHT_UNITS = RUNWAY_UNITS + PIN_HOLD_UNITS;

/** S4 rides document scroll (1.0). Every S3 layer recedes slower so S4 overtakes
 *  it, with a wide enough spread that the depth reads clearly: the front hills
 *  lag noticeably under scroll speed, the content (title + chat) is slower
 *  still, and the deeper planes fall further behind (deeper = slower). */
export const SECTION_THREE_FOREGROUND_HANDOFF_FACTOR = 0.7;
export const SECTION_THREE_SCENE_HANDOFF_FACTOR = 0.5;
export const SECTION_THREE_DISTANT_HANDOFF_FACTOR = 0.3;
export const SECTION_THREE_SKY_HANDOFF_FACTOR = 0.15;

/* Progress 0 used to wait for `scrollY === runwayTop` (sticky stage fully pinned).
 Desktop already slides 1vh over S2, so the world parallax starts at first
 intersection. Mobile starts at Framer `start 0.8` — shortly after the stage enters.
 Cover stays keyed to the pin-relative chat-world end so S4's overlap is unchanged.
 The chat intro arms separately: once the title block is fully inside the viewport,
 so the title/subtitle/chat entrance always plays on screen. */
export const SECTION_THREE_ENTRY_LEAD_DESKTOP_UNITS = 1;
export const SECTION_THREE_ENTRY_LEAD_MOBILE_UNITS = 0.8;

export type TSectionThreeScrollProgress = {
	worldProgress: number;
	entryProgress: number;
	recedeProgress: number;
	coverProgress: number;
	handoffProgress: number;
};

/** Fallback for the pin-relative unit where S4 first meets the fold, used only
 *  when the S4 element cannot be measured. The live value is derived from S4's
 *  measured position so the handoff tracks the actual (capped) cover overlap.
 *  Floored at the minimum pinned dwell: the overlap target alone would land
 *  before the pin once the caps resolve it down. */
export const SECTION_THREE_HANDOFF_START_UNITS = Math.max(
	SECTION_THREE_MIN_PINNED_DWELL_UNITS,
	SECTION_THREE_RUNWAY_HEIGHT_UNITS - SECTION_FOUR_OVERLAP_UNITS - 1,
);

export function mapSectionThreeScrollProgress({
	scrollY,
	runwayTop,
	viewportHeight,
	entryLeadUnits,
	handoffStartUnits,
}: {
	scrollY: number;
	runwayTop: number;
	viewportHeight: number;
	entryLeadUnits: number;
	handoffStartUnits: number;
}): TSectionThreeScrollProgress {
	const unitsFromPin = (scrollY - runwayTop) / Math.max(viewportHeight, 1);
	const unitsFromEntry = unitsFromPin + entryLeadUnits;
	const worldSpan = CHAT_WORLD_UNITS + entryLeadUnits;
	return {
		worldProgress: clamp(unitsFromEntry / worldSpan),
		entryProgress: clamp(unitsFromEntry / entryLeadUnits),
		recedeProgress: Math.max(0, unitsFromPin),
		coverProgress: clamp(unitsFromPin - CHAT_WORLD_UNITS),
		handoffProgress: Math.max(0, unitsFromPin - handoffStartUnits),
	};
}

export type TSectionThreeLayerMotion = {
	sceneY: number;
	skyY: number;
	distantY: number;
	chatY: number;
	foregroundY: number;
};

/**
 * The landscape drifts into its depth pose while the section slides into the
 * pin (entryProgress). The moment the stage touches the top of the viewport,
 * every layer starts receding with scroll — the content (title + subtitle +
 * chat) included, riding the scene — with the depth ordering: front hills
 * fastest, then the content, then the back hills, then the sky. S4 meets the
 * fold a short dwell later and rides document scroll (1.0), overtaking the
 * receding world for the parallax cover.
 */
/** Viewport-space y for each layer — scene + local offsets merged so the stage needs one transform per layer. */
export function mapSectionThreeViewportMotion(
	args: Parameters<typeof mapSectionThreeLayerMotion>[0],
): Pick<TSectionThreeLayerMotion, "skyY" | "distantY" | "chatY" | "foregroundY"> {
	const layer = mapSectionThreeLayerMotion(args);
	return {
		skyY: layer.sceneY + layer.skyY,
		distantY: layer.sceneY + layer.distantY,
		chatY: layer.sceneY + layer.chatY,
		foregroundY: layer.sceneY + layer.foregroundY,
	};
}

export function mapSectionThreeLayerMotion({
	entryProgress,
	recedeProgress,
	isMobile,
	viewportHeight,
	reducedMotion = false,
}: {
	entryProgress: number;
	recedeProgress: number;
	isMobile: boolean;
	viewportHeight: number;
	reducedMotion?: boolean;
}): TSectionThreeLayerMotion {
	/* The drift settles into the pinned pose as the stage reaches the top of the
	 * viewport, then the recede takes over. These depths are where the old world
	 * drift sat at the pin (≈⅓ of its full travel) — drifting to the full depth
	 * by the pin lifts the front hills mid-viewport and strands a flat slab of
	 * foreground below them. */
	const skyDepth = lerp(0, isMobile ? -11 : -18, entryProgress);
	const distantDepth = lerp(0, isMobile ? -70 : -115, entryProgress);
	const foregroundDepth = lerp(0, isMobile ? -157 : -254, entryProgress);
	const recedePx = reducedMotion ? 0 : Math.max(viewportHeight, 1) * Math.max(0, recedeProgress);
	return {
		sceneY: 0 - recedePx * SECTION_THREE_SCENE_HANDOFF_FACTOR,
		skyY: skyDepth + recedePx * (SECTION_THREE_SCENE_HANDOFF_FACTOR - SECTION_THREE_SKY_HANDOFF_FACTOR),
		distantY: distantDepth + recedePx * (SECTION_THREE_SCENE_HANDOFF_FACTOR - SECTION_THREE_DISTANT_HANDOFF_FACTOR),
		chatY: 0,
		foregroundY:
			foregroundDepth + recedePx * (SECTION_THREE_SCENE_HANDOFF_FACTOR - SECTION_THREE_FOREGROUND_HANDOFF_FACTOR),
	};
}

const SECTION_TWO_TITLE_INTRO_MS = 880;
const CHAT_SEQUENCE_MS = computeChatPlaybackDurationMs(SECTION_TWO_CHAT_MESSAGES) + CHAT_PLAYBACK_CLOCK_PAD_MS;
const SECTION_THREE_SCRIM_MAX_OPACITY = 0.5;
/** Cover-phase span for the teal wash. Shorter than the full cover so the colour arrives sooner. */
export const SECTION_THREE_SCRIM_SPAN = 0.4;
/**
 * Day holds through the pin. Evening then tracks the rest of the chat-world
 * scroll 1:1, and is fully in before section four covers the scene.
 */
export const SECTION_THREE_EVENING_FADE_START = 0.48;
/** Remaining world progress after the pin (`1 - start`). */
export const SECTION_THREE_EVENING_FADE_SPAN = 0.52;

/** 0 in daylight at the pin, 1 when the chat-world scroll is finished. */
export function mapSectionThreeEveningOpacity(worldProgress: number): number {
	return linkedProgress(worldProgress, SECTION_THREE_EVENING_FADE_START, SECTION_THREE_EVENING_FADE_SPAN);
}

/** True when the element's box is entirely inside the viewport (no clipping at top or bottom). */
export function isElementFullyVisibleInViewport(
	rect: { top: number; bottom: number },
	viewportHeight: number,
): boolean {
	if (viewportHeight <= 0) return false;
	return rect.top >= 0 && rect.bottom <= viewportHeight;
}

/** Teal scrim waits for the title to be fully on screen, then eases in over a short cover span. */
export function mapSectionThreeScrimOpacity(coverProgress: number, titleFullyVisible: boolean): number {
	if (!titleFullyVisible) return 0;
	const scrimProgress = easeInOut(linkedProgress(coverProgress, 0, SECTION_THREE_SCRIM_SPAN));
	return scrimProgress * SECTION_THREE_SCRIM_MAX_OPACITY;
}

/* z-10 lifts the runway above S2's pinned stage (and stays under S4's z-50). The
 desktop-only negative margin overlaps S2's pin-hold viewport, so this world slides up
 over the settled accordion before pinning; mobile stacks normally. */
export const SECTION_THREE_RUNWAY_CLASS =
	"landing-section-three-runway relative z-10 lg:-mt-[100svh] lg:supports-[height:100dvh]:-mt-[100dvh]";
export const SECTION_THREE_STAGE_CLASS =
	"sticky top-0 z-0 h-[100svh] supports-[height:100dvh]:h-[100dvh] overflow-hidden isolate";

/**
 * Self-contained scroll scene for the pinned landscape/chat world, driven by this
 * section's own runway so the rest of the page is plain stacked flow.
 */
function resolveSectionThreeHandoffStartUnits(
	runwayTop: number,
	viewportHeight: number,
	sectionFour: HTMLElement | null,
): number {
	if (!sectionFour) return SECTION_THREE_HANDOFF_START_UNITS;
	return (sectionFour.offsetTop - viewportHeight - runwayTop) / Math.max(viewportHeight, 1);
}

export function useSectionThreeScroll() {
	const reducedMotion = useReducedMotion();
	const reducedMotionRef = useRef(reducedMotion);
	reducedMotionRef.current = reducedMotion;
	const runwayRef = useRef<HTMLDivElement | null>(null);
	const { scrollY } = useScroll();

	const skyY = useMotionValue(0);
	const distantY = useMotionValue(0);
	const chatY = useMotionValue(0);
	const foregroundY = useMotionValue(0);
	const eveningOpacity = useMotionValue(0);
	const chatScale = useMotionValue(1);
	const sectionThreeScrimOpacity = useMotionValue(0);
	const chatClock = useMotionValue(0);

	const [sectionTwoIntro, setSectionTwoIntro] = useState<TSectionTwoIntro>("idle");
	const [chatStatus, setChatStatus] = useState<TChatStatus>("idle");
	const [runwayUnits, setRunwayUnits] = useState(SECTION_THREE_RUNWAY_HEIGHT_UNITS);

	const sectionTwoIntroTimerRef = useRef(0);
	const chatPlaybackRef = useRef<{ stop: () => void } | null>(null);
	const sectionTwoIntroRef = useRef(sectionTwoIntro);
	sectionTwoIntroRef.current = sectionTwoIntro;
	const sectionTwoPlayedRef = useRef(false);
	const chatArmedRef = useRef(false);
	const titleSeenRef = useRef(false);
	const layoutCacheRef = useRef<TSectionThreeLayoutCache>({
		viewportHeight: 1,
		isMobile: false,
		sectionFourEl: null,
		titleBlockEl: null,
	});

	const refreshLayoutCache = () => {
		if (typeof window === "undefined") return;
		const runway = runwayRef.current;
		if (!runway) return;

		const viewportHeight = Math.max(window.innerHeight, 1);
		const isMobile = window.innerWidth < 768;
		const sectionFour = (runway.getRootNode() as Document | ShadowRoot).querySelector<HTMLElement>(
			"#triffin-section-four",
		);
		layoutCacheRef.current = {
			viewportHeight,
			isMobile,
			sectionFourEl: sectionFour,
			titleBlockEl: runway.querySelector<HTMLElement>(`#${SECTION_TWO_TITLE_BLOCK_ID}`),
		};
	};

	const refreshLayoutCacheRef = useRef(refreshLayoutCache);
	refreshLayoutCacheRef.current = refreshLayoutCache;

	const applyScene = () => {
		if (typeof window === "undefined") return;
		const runway = runwayRef.current;
		if (!runway) return;

		const { viewportHeight, isMobile, sectionFourEl, titleBlockEl } = layoutCacheRef.current;
		/* offsetTop shifts when content above reflows without resizing this runway — read it
		 * each frame; the expensive querySelector/getBoundingClientRect stays cached. */
		const runwayTop = runway.offsetTop;
		const handoffStartUnits = resolveSectionThreeHandoffStartUnits(runwayTop, viewportHeight, sectionFourEl);
		const { worldProgress, entryProgress, recedeProgress, handoffProgress } = mapSectionThreeScrollProgress({
			scrollY: scrollY.get(),
			runwayTop,
			viewportHeight,
			entryLeadUnits: isMobile ? SECTION_THREE_ENTRY_LEAD_MOBILE_UNITS : SECTION_THREE_ENTRY_LEAD_DESKTOP_UNITS,
			handoffStartUnits,
		});

		const eveningOpacityValue = mapSectionThreeEveningOpacity(worldProgress);
		const titleBlockFullyVisible =
			!chatArmedRef.current &&
			titleBlockEl !== null &&
			isElementFullyVisibleInViewport(titleBlockEl.getBoundingClientRect(), viewportHeight);
		/* The gate latches: the parallax recede carries the title off the top
		 * mid-cover, and the scrim must not pop off with it. The ramp keys off
		 * handoffProgress — S4's measured cover position — not the fixed
		 * pin-relative cover phase, which no longer matches the real cover. */
		if (titleBlockFullyVisible) titleSeenRef.current = true;
		sectionThreeScrimOpacity.set(mapSectionThreeScrimOpacity(handoffProgress, titleSeenRef.current));

		const viewportMotion = mapSectionThreeViewportMotion({
			entryProgress,
			recedeProgress,
			isMobile,
			viewportHeight,
			reducedMotion: Boolean(reducedMotionRef.current),
		});

		if (!chatArmedRef.current && titleBlockFullyVisible) {
			chatArmedRef.current = true;
			if (sectionTwoPlayedRef.current) {
				sectionTwoIntroRef.current = "chat-playing";
				startTransition(() => {
					setSectionTwoIntro("chat-playing");
					setChatStatus("complete");
				});
			} else {
				sectionTwoPlayedRef.current = true;
				sectionTwoIntroRef.current = "title-playing";
				startTransition(() => setSectionTwoIntro("title-playing"));
				sectionTwoIntroTimerRef.current = window.setTimeout(
					() => {
						sectionTwoIntroTimerRef.current = 0;
						sectionTwoIntroRef.current = "chat-playing";
						setSectionTwoIntro("chat-playing");
						setChatStatus("playing");
						const chatDurationMs = reducedMotionRef.current ? 120 : CHAT_SEQUENCE_MS;
						chatClock.set(0);
						chatPlaybackRef.current?.stop();
						let cancelled = false;
						const playback = animate(chatClock, 1, { duration: chatDurationMs / 1000, ease: "linear" });
						chatPlaybackRef.current = {
							stop: () => {
								cancelled = true;
								playback.stop();
							},
						};
						void playback.then(() => {
							if (!cancelled) setChatStatus("complete");
						});
					},
					reducedMotionRef.current ? 0 : SECTION_TWO_TITLE_INTRO_MS,
				);
			}
		}

		skyY.set(viewportMotion.skyY);
		distantY.set(viewportMotion.distantY);
		chatY.set(viewportMotion.chatY);
		foregroundY.set(viewportMotion.foregroundY);
		eveningOpacity.set(eveningOpacityValue);
		chatScale.set(1);
	};

	const applySceneRef = useRef(applyScene);
	applySceneRef.current = applyScene;
	const applySceneRafRef = useRef(0);

	useMotionValueEvent(scrollY, "change", () => {
		if (applySceneRafRef.current !== 0) return;
		applySceneRafRef.current = window.requestAnimationFrame(() => {
			applySceneRafRef.current = 0;
			applySceneRef.current();
		});
	});

	useLayoutEffect(() => {
		const runway = runwayRef.current;
		const onLayoutChange = () => {
			refreshLayoutCacheRef.current();
			applySceneRef.current();
		};
		onLayoutChange();
		window.addEventListener("resize", onLayoutChange);
		const observers: ResizeObserver[] = [];
		if (typeof ResizeObserver !== "undefined" && runway) {
			const runwayObserver = new ResizeObserver(onLayoutChange);
			runwayObserver.observe(runway);
			observers.push(runwayObserver);
			const sectionFour = (runway.getRootNode() as Document | ShadowRoot).querySelector<HTMLElement>(
				"#triffin-section-four",
			);
			if (sectionFour) {
				const sectionFourObserver = new ResizeObserver(onLayoutChange);
				sectionFourObserver.observe(sectionFour);
				observers.push(sectionFourObserver);
			}
		}
		return () => {
			window.removeEventListener("resize", onLayoutChange);
			for (const observer of observers) observer.disconnect();
			if (applySceneRafRef.current !== 0) {
				window.cancelAnimationFrame(applySceneRafRef.current);
				applySceneRafRef.current = 0;
			}
			chatPlaybackRef.current?.stop();
			if (sectionTwoIntroTimerRef.current !== 0) window.clearTimeout(sectionTwoIntroTimerRef.current);
		};
	}, []);

	/* The runway is sized to S4's measured height + one viewport + the pinned dwell,
	 so S4's edge meets the fold the same beat after the pin on any viewport height
	 (a fixed runway drifts later on taller screens, where S4's height in viewport
	 units shrinks). Measured via ResizeObserver because remote logos/portrait can
	 grow S4 after mount. */
	useLayoutEffect(() => {
		const runway = runwayRef.current;
		if (!runway) return;
		const sectionFour = (runway.getRootNode() as Document | ShadowRoot).querySelector<HTMLElement>(
			"#triffin-section-four",
		);
		if (!sectionFour) return;
		const measure = () => {
			refreshLayoutCacheRef.current();
			const viewportHeight = Math.max(window.innerHeight, 1);
			setRunwayUnits(sectionFour.offsetHeight / viewportHeight + 1 + SECTION_THREE_MIN_PINNED_DWELL_UNITS);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(sectionFour);
		window.addEventListener("resize", measure);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, []);

	return {
		runwayRef,
		runwayUnits,
		scene: {
			skyY,
			distantY,
			chatY,
			foregroundY,
			eveningOpacity,
			chatScale,
			sectionThreeScrimOpacity,
			sectionTwoIntro,
			chatStatus,
		} satisfies TSectionThreeScene,
	};
}
